/**
 * Сессия: кто залогинен (участок ЛК и/или админ), меню и настройки сайта.
 *
 * Авторизация держится на токенах (api/tokens.ts), а не на cookie. Поэтому сессия
 * собирается из двух запросов, а не из одного: у жителя и админа РАЗНЫЕ токены, а в
 * заголовок Authorization помещается только один. GET /api/auth/session, подписанный
 * жительским токеном, отдаёт участок, меню и настройки сайта; кто админ — знает
 * GET /api/admin/me, подписанный админским. Человек может быть залогинен обоими сразу
 * (в legacy это давали две разные cookie).
 */

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'

import { api } from '@/api/client'
import { clearTokens, getRefreshToken, hasSession } from '@/api/tokens'
import { clearCache } from '@/lib/apiCache'
import type { Area, SntUser } from '@/api/types'

interface AdminInfo {
  id: number
  login: string
  superadmin: boolean
}

/** Пункт меню как его отдаёт Modules::menu(): link = modules.module (URL-сегмент). */
export interface MenuItem {
  link: string
  name: string
}

interface SessionData {
  user: Area | null
  /** Владелец участка — его ФИО legacy показывает в шапке (.fioblock). */
  owner: SntUser | null
  admin: AdminInfo | null
  menu: MenuItem[]
  lk_menu: MenuItem[]
  site: Record<string, string>
}

interface SessionContextValue extends SessionData {
  loading: boolean
  /** Перечитать сессию (после логина/выхода). */
  refresh: () => Promise<void>
  /** Выход жителя. */
  logout: () => Promise<void>
  /** Выход админа (отдельная сессия — отдельный выход). */
  logoutAdmin: () => Promise<void>
}

const EMPTY: SessionData = { user: null, owner: null, admin: null, menu: [], lk_menu: [], site: {} }

const SessionContext = createContext<SessionContextValue | null>(null)

/** Ответ GET /api/admin/me — нас интересует только, кто это. */
interface AdminMe {
  admin: AdminInfo
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [data, setData] = useState<SessionData>(EMPTY)
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    try {
      // Публичный роут: без токена отдаёт меню и настройки сайта, с жительским — ещё и участок.
      const session = await api.get<SessionData>('/api/auth/session')

      let admin: AdminInfo | null = null
      if (hasSession('admin')) {
        try {
          admin = (await api.get<AdminMe>('/api/admin/me')).admin
        } catch {
          admin = null   // токен админа мёртв, а продлить не вышло — просто не админ
        }
      }

      setData({ ...session, admin })
    } catch {
      setData(EMPTY)
    } finally {
      setLoading(false)
    }
  }, [])

  const logout = useCallback(async () => {
    try {
      await api.post('/api/auth/logout', { refresh_token: getRefreshToken('user') })
    } catch {
      // связи нет — локальную сессию всё равно закрываем
    }
    clearTokens('user')
    clearCache()          // кеш страниц принадлежал вышедшему участку
    await refresh()
  }, [refresh])

  const logoutAdmin = useCallback(async () => {
    try {
      await api.post('/api/admin/auth/logout', { refresh_token: getRefreshToken('admin') })
    } catch {
      // связи нет — локальную сессию всё равно закрываем
    }
    clearTokens('admin')
    clearCache()          // кеш админки принадлежал вышедшему админу
    await refresh()
  }, [refresh])

  useEffect(() => {
    void refresh()
  }, [refresh])

  return (
    <SessionContext.Provider value={{ ...data, loading, refresh, logout, logoutAdmin }}>
      {children}
    </SessionContext.Provider>
  )
}

export function useSession(): SessionContextValue {
  const context = useContext(SessionContext)
  if (context === null) throw new Error('useSession вне SessionProvider')
  return context
}
